import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import {
    FormErrors,
    getFormAsyncErrors,
    getFormSyncErrors,
    hasSubmitSucceeded,
    isInvalid,
    isPristine,
    isSubmitting,
} from 'redux-form';
import { CannedQuestionName } from '../../api/models/survey';
import {
    CheckoutFormSection,
    CHECKOUT_FORM_NAME,
    ORGANIZER_QUESTIONS_FORM_NAME,
} from '../../constants';
import {
    CARD_EXPIRATION_DATE_FIELD,
    CARD_POSTAL_CODE_FIELD,
    CARD_SECURITY_CODE_FIELD,
    CREDIT_CARD_NUMBER_FIELD,
} from '../../containers/checkout/constants';
import { PaymentMethodType } from '../../models/paymentMethods/enums';
import { BUYER_ID } from '../../models/survey';
import { CheckoutState } from '../../state';
import { CheckoutFormData, FormNamespace } from '../../state/form';
import {
    filterErroredFieldsFromFormValues,
    getActiveFieldsPerSectionNamespace,
    hasErrorInFields,
} from '../../utils/forms';
import { isAttendeeField } from '../../utils/surveys/typedSurveyUtils';
import { isPaidOrder } from '../order';
import { canRetrySubmit, getAttendees } from '../order/typedOrderSelectors';
import {
    getIsPaymentMethodInfoMissing,
    getSelectedPaymentMethod,
} from '../payment';
import { isMyOrderFormPage, isOrganizerQuestionsPage } from '../routing';
import { getNamespaceValue } from '../utils/selector';

export const isFormSubmitting = (state: CheckoutState, formName: string) =>
    isSubmitting(formName)(state);

export const isFormSuccessfullySubmitted = (
    state: CheckoutState,
    formName: string,
) => hasSubmitSucceeded(formName)(state);

export const isFormSubmitted = (state: CheckoutState, formName: string) =>
    isFormSubmitting(state, formName) ||
    isFormSuccessfullySubmitted(state, formName);

export const isSubmitDisabled = (
    state: CheckoutState,
    formName: string,
    disableOnSucceeded = true,
) =>
    isPristine(formName)(state) ||
    isFormSubmitting(state, formName) ||
    (disableOnSucceeded && isFormSuccessfullySubmitted(state, formName)) ||
    isInvalid(formName)(state);

export const getRegisteredFields = (state: CheckoutState, formName: string) => {
    return Object.keys(
        state.form[formName as keyof FormNamespace]?.registeredFields ?? {},
    );
};

export const isCheckoutFormSubmitDisabled = (state: CheckoutState) => {
    // If submit is disabled (loading) this takes precedence over any other condition
    if (isSubmitDisabled(state, CHECKOUT_FORM_NAME)) {
        return true;
    }

    // If the submit can be retried then the button must be enabled so we can't disable it
    if (canRetrySubmit(state)) {
        return false;
    }

    // The remaining conditions are payment-related, which make no sense if it isn't a paid order
    if (!isPaidOrder(state)) {
        return false;
    }

    return getIsPaymentMethodInfoMissing(state);
};

export const getKeepDisabledStyleWhileLoading = (
    state: CheckoutState,
    formName: string,
) =>
    isSubmitDisabled(state, formName) &&
    !isFormSubmitting(state, formName) &&
    !isFormSuccessfullySubmitted(state, formName);

const getContactInfoError = (
    fields: Record<string, any>,
    syncErrors: FormErrors<{}, string>,
    asyncErrors: FormErrors<{}, string>,
) =>
    hasErrorInFields(
        fields.buyer,
        get(syncErrors, BUYER_ID),
        get(asyncErrors, BUYER_ID),
    )
        ? [CheckoutFormSection.contactInfo]
        : [];

const getToSError = (
    fields: Record<string, any>,
    errors: FormErrors<{}, string>,
) =>
    !!get(fields, 'tosCheckbox') && !!get(errors, 'tosCheckbox')
        ? [CheckoutFormSection.tosCheckbox]
        : [];

const getAttendeesError = (errors: FormErrors<{}, string>) => {
    const attendeeSurveysErrors = pickBy(errors, (_, key) =>
        isAttendeeField(key),
    );
    return Object.values(attendeeSurveysErrors).some((attendeeSurvey) =>
        Object.values(attendeeSurvey).some((field) => !!field),
    )
        ? [CheckoutFormSection.attendees]
        : [];
};

const getPaymentError = (
    fields: Record<string, any>,
    syncErrors: FormErrors<{}, string>,
    asyncErrors: FormErrors<{}, string>,
    state: CheckoutState,
) => {
    if (!isPaidOrder(state)) {
        return [];
    }

    return getSelectedPaymentMethod(state) === PaymentMethodType.NOT_SELECTED ||
        hasErrorInFields(
            fields.payment,
            get(syncErrors, 'payment'),
            get(asyncErrors, 'payment'),
        )
        ? [CheckoutFormSection.payment]
        : [];
};

export const getCheckoutFormSectionsWithError = (
    state: CheckoutState,
    formName: string,
) => {
    const formSyncErrors = getFormSyncErrors(formName)(state);
    const formAsyncErrors = getFormAsyncErrors(formName)(state);
    const transformedFields = getActiveFieldsPerSectionNamespace(
        state.form[formName as keyof FormNamespace]?.registeredFields,
    );

    if ((!formSyncErrors && !formAsyncErrors) || isEmpty(transformedFields)) {
        return [];
    }

    return [
        ...getContactInfoError(
            transformedFields,
            formSyncErrors,
            formAsyncErrors,
        ),
        ...getAttendeesError(formSyncErrors),
        ...getToSError(transformedFields, formSyncErrors),
        ...getPaymentError(
            transformedFields,
            formSyncErrors,
            formAsyncErrors,
            state,
        ),
    ];
};

export const isFieldRegistered = (
    state: CheckoutState,
    fieldName: string,
    formName: string,
) =>
    get(
        state.form?.[formName as keyof FormNamespace]?.registeredFields,
        fieldName,
    )?.count > 0;

export const getFormValues = (state: CheckoutState, formName: string) => {
    return state.form?.[formName as keyof FormNamespace]?.values || {};
};

export const getFormValuesWithoutSyncValidationErroredFields = (
    state: CheckoutState,
    formName: string,
) => {
    const formValues = getFormValues(state, formName);
    const formSyncErrors = getFormSyncErrors(formName)(state);

    if (isEmpty(formSyncErrors) || isEmpty(formValues)) return formValues;

    return filterErroredFieldsFromFormValues(formValues, formSyncErrors);
};

export const getFormName = (state: CheckoutState) =>
    isOrganizerQuestionsPage(state) || isMyOrderFormPage(state)
        ? ORGANIZER_QUESTIONS_FORM_NAME
        : CHECKOUT_FORM_NAME;

export const getAttendeeAnswers = (
    state: CheckoutState,
    attendeeId: string,
    formName: string,
) => getFormValues(state, formName)[attendeeId as keyof CheckoutFormData] || {};

export const getAttendeeFirstName = (
    state: CheckoutState,
    attendeeId: string,
) =>
    getAttendeeAnswers(state, attendeeId, CHECKOUT_FORM_NAME)?.[
        CannedQuestionName.firstName
    ] || null;

export const getFormServerError = (
    state: CheckoutState,
    formName: keyof FormNamespace,
) => get(state, `form.${formName}.error`) || null;

export const hasFormServerError = (
    state: CheckoutState,
    formName: keyof FormNamespace,
) => {
    const formServerError = getFormServerError(state, formName);
    return !!formServerError;
};

export const getCopyDataCheckboxValue = (state: CheckoutState) => {
    const attendees = getAttendees(state);
    const attendeeId = attendees[0]?.id;
    const formValues = getFormValues(state, 'checkoutForm');
    return formValues[attendeeId]?.copyBillingInfo;
};

export enum SavedPaymentAnalyticsFeatureStateType {
    /** this feature was not part of the attendee experience */
    notUsed = 'notUsed',
    /** the attendee paid with a saved card */
    savedCard = 'savedCard',
    /** the attendee paid with a new card and left the save card checkbox unselected */
    newCard = 'newCard',
    /** the attendee paid with a new card and left the save card checkbox selected */
    savedNewCard = 'savedNewCard',
    /** not enough data in the state, we can't tell */
    undefined = 'undefined',
}

export const getSavedPaymentAnalyticsFeatureState = (
    state: CheckoutState,
): SavedPaymentAnalyticsFeatureStateType => {
    const payment = get(state, `form.${CHECKOUT_FORM_NAME}.values.payment`);
    const paymentMethod = get(
        state,
        `form.${CHECKOUT_FORM_NAME}.values.paymentMethod`,
    );

    if (isEmpty(payment))
        return SavedPaymentAnalyticsFeatureStateType.undefined;
    if (paymentMethod !== PaymentMethodType.CREDIT || !payment.vaultId)
        return SavedPaymentAnalyticsFeatureStateType.notUsed;

    if (payment.saveCreditCard)
        return SavedPaymentAnalyticsFeatureStateType.savedNewCard;

    if (payment.vaultId === PaymentMethodType.CREDIT)
        return SavedPaymentAnalyticsFeatureStateType.newCard;

    return SavedPaymentAnalyticsFeatureStateType.savedCard;
};

export const getBillingZipCode = (state: CheckoutState) => {
    const userInstruments = getNamespaceValue(
        state.user,
        'userInstruments',
        [],
    );

    const {
        form: { checkoutForm: { values: { payment = {} } = {} } = {} },
    } = state;

    const cardPostalCode = payment.cardPostalCode;

    if (cardPostalCode) {
        return cardPostalCode;
    }

    const vaultId =
        'vaultId' in payment ? (payment as { vaultId?: string }).vaultId : null;

    if (vaultId) {
        const selectedInstrument = userInstruments.find(
            (instrument) => instrument.vaultId === vaultId,
        );

        if (selectedInstrument) {
            return selectedInstrument.billingInformation.address.postalCode;
        }
    }

    return '';
};

export const getFirstAttendeeWithSurvey = (state: CheckoutState) => {
    const attendees = getNamespaceValue(state.order, 'attendees', []);

    return attendees.find(
        (attendee) => attendee.survey && attendee.survey.length,
    );
};

export const getBuyerEmailFromSurveyData = (state: CheckoutState) =>
    state.form[CHECKOUT_FORM_NAME]?.values?.buyer?.['N-email'];

export const getBuyerFirstNameFromSurveyData = (state: CheckoutState) =>
    state.form[CHECKOUT_FORM_NAME]?.values?.buyer?.['N-first_name'];

export const getBuyerLastNameFromSurveyData = (state: CheckoutState) =>
    state.form[CHECKOUT_FORM_NAME]?.values?.buyer?.['N-last_name'];

const hasErrorInField = ({
    fieldName,
    formFields,
    formSyncErrors,
}: {
    fieldName: string;
    formFields: Record<string, any>;
    formSyncErrors: FormErrors<{}, string>;
}) => {
    const isTouched = !!get(formFields, fieldName)?.touched;
    const hasError = !!get(formSyncErrors, fieldName);

    return isTouched && hasError;
};

export const getPaymentFieldsWithError = (
    state: CheckoutState,
    formName: string,
) => {
    const formFields = get(state.form[CHECKOUT_FORM_NAME], 'fields');
    const formSyncErrors = getFormSyncErrors(formName)(state);

    return {
        [CREDIT_CARD_NUMBER_FIELD]: hasErrorInField({
            fieldName: `payment.${CREDIT_CARD_NUMBER_FIELD}`,
            formFields,
            formSyncErrors,
        }),
        [CARD_EXPIRATION_DATE_FIELD]: hasErrorInField({
            fieldName: `payment.${CARD_EXPIRATION_DATE_FIELD}`,
            formFields,
            formSyncErrors,
        }),
        [CARD_SECURITY_CODE_FIELD]: hasErrorInField({
            fieldName: `payment.${CARD_SECURITY_CODE_FIELD}`,
            formFields,
            formSyncErrors,
        }),
        [CARD_POSTAL_CODE_FIELD]: hasErrorInField({
            fieldName: `payment.${CARD_POSTAL_CODE_FIELD}`,
            formFields,
            formSyncErrors,
        }),
    };
};
